import React from "react";
import { FREIGHT_ALLOWANCE_OPTIONS } from "../lib/mandiSettlement";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export default function SettlementReviewTable({
  title,
  subtitle,
  rows,
  form,
  onUpdate,
  onUpdateAllocationRate,
  onUpdateContractTerms,
  optionsFor,
}) {
  return (
    <div
      className="rounded-md border border-border bg-card overflow-hidden min-w-0"
      data-testid={`settlement-review-${title}`}
    >
      <div className="px-3 py-2.5 bg-muted/60 border-b border-border">
        <div className="text-sm font-medium">{title}</div>
        {subtitle && <div className="text-[10px] text-muted-foreground mt-0.5 break-words">{subtitle}</div>}
      </div>
      <div className="divide-y divide-border/80">
        {rows.map((row) => (
          <div
            key={row.key}
            className={`p-3 sm:p-4 space-y-2.5 min-w-0 ${
              row.total ? "bg-sky-50/50" : row.highlight ? "bg-muted/30" : ""
            }`}
          >
            <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
              <p className={`text-sm ${row.total ? "font-semibold" : "font-medium"} text-foreground`}>
                {row.particular}
              </p>
              <p
                className={`text-sm tabular shrink-0 ${
                  row.total ? "font-semibold text-primary" : "text-foreground"
                }`}
              >
                {row.final}
              </p>
            </div>

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-xs min-w-0">
              <div className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  Contract condition
                </dt>
                <dd className="min-w-0">
                  {row.conditionEdit ? (
                    <ReviewConditionCell
                      conditionEdit={row.conditionEdit}
                      form={form}
                      onUpdate={onUpdate}
                      onUpdateContractTerms={onUpdateContractTerms}
                      optionsFor={optionsFor}
                    />
                  ) : (
                    <span className="text-muted-foreground break-words">{row.condition || "—"}</span>
                  )}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  As per contract
                </dt>
                <dd className="tabular break-words">{row.contractValue}</dd>
              </div>
              {row.edit && (
                <div className="min-w-0 sm:col-span-2">
                  <dt className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                    Adjust
                  </dt>
                  <dd className="max-w-xs">
                    <ReviewEditCell
                      edit={row.edit}
                      form={form}
                      onUpdate={onUpdate}
                      onUpdateAllocationRate={onUpdateAllocationRate}
                    />
                  </dd>
                </div>
              )}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewConditionCell({ conditionEdit, form, onUpdate, onUpdateContractTerms, optionsFor }) {
  if (!conditionEdit) return <span className="text-muted-foreground">—</span>;

  if (conditionEdit.type === "select" && conditionEdit.field === "deduction_scope") {
    return (
      <Select value={form?.deduction_scope || "COMPLETE"} onValueChange={(v) => onUpdate("deduction_scope", v)}>
        <SelectTrigger className="h-8 text-xs w-full max-w-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="COMPLETE">Full quantity</SelectItem>
          <SelectItem value="REMAINING">Remaining only</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (conditionEdit.type === "select" && conditionEdit.field === "price_cut_basis") {
    return (
      <Select
        value={form?.price_cut_basis || "GROSS_PCT"}
        onValueChange={(v) => onUpdate("price_cut_basis", v)}
      >
        <SelectTrigger className="h-8 text-xs w-full max-w-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="GROSS_PCT">% Gross</SelectItem>
          <SelectItem value="PER_QTL">Per qtl (₹)</SelectItem>
        </SelectContent>
      </Select>
    );
  }

  if (conditionEdit.type === "cd_root") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap">
          <Checkbox
            checked={Boolean(form?.cd_applicable)}
            onCheckedChange={(v) => onUpdate("cd_applicable", v === true)}
          />
          CD
        </label>
        <Input
          type="number"
          step="0.1"
          min="0"
          className="h-8 w-20 text-xs"
          disabled={!form?.cd_applicable}
          value={form?.cd_percent ?? ""}
          onChange={(e) => onUpdate("cd_percent", e.target.value)}
          placeholder="%"
        />
      </div>
    );
  }

  if (conditionEdit.type === "brokerage_root") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap">
          <Checkbox
            checked={Boolean(form?.broker_applicable)}
            onCheckedChange={(v) => onUpdate("broker_applicable", v === true)}
          />
          Broker
        </label>
        <select
          className="h-8 rounded-md border border-input bg-background px-1.5 text-[10px]"
          disabled={!form?.broker_applicable}
          value={form?.brokerage_basis || "GROSS_PCT"}
          onChange={(e) => onUpdate("brokerage_basis", e.target.value)}
        >
          <option value="GROSS_PCT">% Gross</option>
          <option value="PER_QTL">₹/qtl</option>
        </select>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="h-8 w-24 text-xs"
          disabled={!form?.broker_applicable}
          value={form?.brokerage ?? ""}
          onChange={(e) => onUpdate("brokerage", e.target.value)}
          placeholder={form?.brokerage_basis === "PER_QTL" ? "₹/qtl" : "%"}
        />
        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap">
          <Checkbox
            checked={Boolean(form?.deduct_brokerage_from_receivable)}
            onCheckedChange={(v) => onUpdate("deduct_brokerage_from_receivable", v === true)}
          />
          Deduct from buyer
        </label>
      </div>
    );
  }

  if (conditionEdit.type === "bardana_root") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap">
          <Checkbox
            checked={form?.apply_bag_cuts !== false}
            onCheckedChange={(v) => onUpdate("apply_bag_cuts", v === true)}
          />
          Apply
        </label>
        <Input
          type="number"
          step="1"
          min="0"
          className="h-8 w-20 text-xs"
          disabled={form?.apply_bag_cuts === false}
          value={form?.bardana_gm ?? ""}
          onChange={(e) => onUpdate("bardana_gm", e.target.value)}
          placeholder="gm"
        />
        <select
          className="h-8 rounded-md border border-input bg-background px-1.5 text-[10px]"
          disabled={form?.apply_bag_cuts === false}
          value={form?.bardana_basis || "PER_BAG"}
          onChange={(e) => onUpdate("bardana_basis", e.target.value)}
        >
          <option value="PER_BAG">per bag</option>
          <option value="PER_QTL">per qtl</option>
        </select>
      </div>
    );
  }

  if (conditionEdit.type === "gst_root") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="h-8 rounded-md border border-input bg-background px-1.5 text-[10px]"
          value={form?.gst_type || "NONE"}
          onChange={(e) => onUpdate("gst_type", e.target.value)}
        >
          <option value="NONE">No GST</option>
          <option value="CGST_SGST">CGST+SGST</option>
          <option value="IGST">IGST</option>
        </select>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="h-8 w-20 text-xs"
          disabled={(form?.gst_type || "NONE") === "NONE"}
          value={form?.gst_rate ?? ""}
          onChange={(e) => onUpdate("gst_rate", e.target.value)}
          placeholder="%"
        />
      </div>
    );
  }

  const cid = conditionEdit.contractId;
  const terms = form?.contract_terms?.[String(cid)] || {};

  if (conditionEdit.type === "bardana_deduction") {
    return (
      <Select
        value={terms.bardana_deduction || ""}
        onValueChange={(v) => onUpdateContractTerms(cid, { bardana_deduction: v })}
      >
        <SelectTrigger className="h-8 text-xs w-full max-w-xs"><SelectValue placeholder="Bardana rule…" /></SelectTrigger>
        <SelectContent>
          {(optionsFor("bardana_deduction") || []).map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (conditionEdit.type === "cd") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap">
          <Checkbox
            checked={Boolean(terms.cd_applicable)}
            onCheckedChange={(v) => onUpdateContractTerms(cid, { cd_applicable: v === true })}
          />
          CD
        </label>
        <Input
          type="number"
          step="0.1"
          min="0"
          className="h-8 w-20 text-xs"
          disabled={!terms.cd_applicable}
          value={terms.cd_percent ?? ""}
          onChange={(e) => onUpdateContractTerms(cid, { cd_percent: e.target.value })}
          placeholder="%"
        />
      </div>
    );
  }

  if (conditionEdit.type === "brokerage") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-[10px] whitespace-nowrap">
          <Checkbox
            checked={Boolean(terms.broker_applicable)}
            onCheckedChange={(v) => onUpdateContractTerms(cid, { broker_applicable: v === true })}
          />
          Broker
        </label>
        <Input
          type="number"
          step="1"
          min="0"
          className="h-8 w-24 text-xs"
          disabled={!terms.broker_applicable}
          value={terms.brokerage ?? ""}
          onChange={(e) => onUpdateContractTerms(cid, { brokerage: e.target.value })}
          placeholder="₹"
        />
      </div>
    );
  }

  if (conditionEdit.type === "freight") {
    return (
      <div className="flex flex-col gap-1.5 max-w-xs">
        <Select
          value={terms.delivery_condition || ""}
          onValueChange={(v) => onUpdateContractTerms(cid, { delivery_condition: v })}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Delivery…" /></SelectTrigger>
          <SelectContent>
            {(optionsFor("delivery_condition") || []).map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="number"
          step="0.01"
          min="0"
          className="h-8 text-xs"
          value={terms.freight_rate_per_qtl ?? ""}
          onChange={(e) => onUpdateContractTerms(cid, { freight_rate_per_qtl: e.target.value })}
          placeholder="Freight ₹/qtl"
        />
      </div>
    );
  }

  return <span className="text-muted-foreground">—</span>;
}

function ReviewEditCell({ edit, form, onUpdate, onUpdateAllocationRate }) {
  if (!edit) {
    return <span className="text-muted-foreground">—</span>;
  }

  if (edit.field === "allocation_rates" && edit.contractId != null) {
    const cid = String(edit.contractId);
    return (
      <Input
        type="number"
        step={edit.step || "0.01"}
        min={edit.min ?? 0}
        className="h-8 text-xs w-full max-w-[140px]"
        placeholder="₹/qtl"
        value={form?.allocation_rates?.[cid] ?? ""}
        onChange={(e) => onUpdateAllocationRate(edit.contractId, e.target.value)}
      />
    );
  }

  if (edit.type === "freight_allowance") {
    return (
      <Select value={form?.freight_allowance_code || "_none"} onValueChange={(v) => onUpdate("freight_allowance_code", v === "_none" ? "" : v)}>
        <SelectTrigger className="h-8 text-xs w-full max-w-xs"><SelectValue placeholder="Allowance…" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">No allowance</SelectItem>
          {FREIGHT_ALLOWANCE_OPTIONS.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  const value = form?.[edit.field] ?? "";
  return (
    <Input
      type={edit.type || "text"}
      step={edit.step}
      min={edit.min}
      className="h-8 text-xs w-full max-w-[140px]"
      placeholder={edit.placeholder || ""}
      value={value}
      onChange={(e) => onUpdate(edit.field, e.target.value)}
    />
  );
}
